/**
 * ListDiscoveryRow - List-specific result row for `CardSearchPanel`
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { IoAdd } from 'react-icons/io5';

import CardHoverPreview from '@/components/CardHoverPreview/CardHoverPreview';
import PrintingSelector from '@/components/CardSearchPanel/PrintingSelector';
import useCardIndex from '@/hooks/useCardIndex';
import { useAllOwnedPrintingsForName } from '@/hooks/useCollectionAvailability';
import type { MinimalCard } from '@/services/cardDatabase';

export interface ListDiscoveryRowProps {
  card: MinimalCard;
  /** scryfall_ids already on the list. */
  listedScryfallIds: Set<string>;
  isPending: boolean;
  onAdd: (card: MinimalCard) => void;
  isHighlighted: boolean;
  onMouseEnter: () => void;
}

export default function ListDiscoveryRow({
  card,
  listedScryfallIds,
  isPending,
  onAdd,
  isHighlighted,
  onMouseEnter,
}: ListDiscoveryRowProps): ReactElement {
  const [selectedCard, setSelectedCard] = useState<MinimalCard>(card);
  const { getAllPrintingsByName } = useCardIndex();
  const ownedPrintings = useAllOwnedPrintingsForName(card.name_lower);

  useEffect(() => {
    setSelectedCard(card);
  }, [card]);

  const isOnList = listedScryfallIds.has(selectedCard.id);

  return (
    <div
      onMouseEnter={onMouseEnter}
      className={clsx('group px-3 py-2.5 transition-colors', isHighlighted ? 'bg-storm' : 'hover:bg-storm/50')}
    >
      <div className="mb-2 flex items-start gap-2">
        <CardHoverPreview imageUris={selectedCard.image_uris ?? null} name={selectedCard.name}>
          <div className="h-14 w-10 shrink-0 overflow-hidden rounded shadow-md">
            {selectedCard.image_uris?.small ? (
              <img src={selectedCard.image_uris.small} alt="" className="h-full w-full object-cover" loading="lazy" />
            ) : (
              <div className="bg-slate flex h-full w-full items-center justify-center">
                <span className="text-ash text-xs">?</span>
              </div>
            )}
          </div>
        </CardHoverPreview>

        <div className="min-w-0 flex-1">
          <p className="text-pearl truncate text-sm leading-tight font-medium">{card.name}</p>
          <p className="text-ash mt-0.5 truncate text-xs">{selectedCard.type_line}</p>
          <p className="mt-1.5 text-xs">
            {ownedPrintings.size > 0 ? (
              <span className="text-silver">
                Owned <span className="text-aether font-medium">{Array.from(ownedPrintings.values()).reduce((a, b) => a + b, 0)}</span>
              </span>
            ) : (
              <span className="text-ash/60 italic">Not in collection</span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <PrintingSelector
          value={selectedCard}
          printings={getAllPrintingsByName(card.name)}
          ownedPrintings={ownedPrintings}
          onChange={setSelectedCard}
          className="flex-1"
        />

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAdd(selectedCard);
          }}
          disabled={isPending || isOnList}
          title={isOnList ? 'This printing is already on the list' : undefined}
          className={clsx(
            'flex h-7 items-center gap-1 rounded px-2.5 text-xs font-medium transition-all',
            isOnList
              ? 'cursor-not-allowed bg-zinc-700 text-zinc-500'
              : 'bg-aether hover:bg-aether/80 text-white opacity-60 group-hover:opacity-100',
            isHighlighted && !isOnList && 'opacity-100',
            isPending && 'opacity-50',
          )}
        >
          <IoAdd className="h-3.5 w-3.5" />
          <span>{isOnList ? 'In list' : 'Add'}</span>
        </button>
      </div>
    </div>
  );
}
